import { Download, FileDown } from 'lucide-react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '@nucleus-commerce/admin-kit/date-time';
import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { ExportKind } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useCreateExport } from '../hooks/use-create-export';
import { useDownloadExport } from '../hooks/use-download-export';
import { useTrackedExport } from '../hooks/use-tracked-export';
import { exportFormSchema, toExportBody, type ExportFormValues } from '../schemas/job-forms';
import type { TrackedJob } from '../schemas/job-rules';

import { TrackedJobStatus } from './tracked-job-status';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['kind', 'from', 'to']);
const mapField = (field: string) => field.replace(/^filters\./, '');

export interface ExportFormCardProps {
  storeId: string;
  defaultKind: ExportKind | undefined;
}

/** Starts an export and follows it until the file can be downloaded. */
export function ExportFormCard({ storeId, defaultKind }: ExportFormCardProps) {
  const { t } = useTranslation('importsExports');
  const format = useFormat();
  const message = useErrorMessage();
  const create = useCreateExport(storeId);
  const download = useDownloadExport(storeId);
  const [tracked, setTracked] = useState<TrackedJob | null>(null);
  const tracker = useTrackedExport(storeId, tracked);

  const form = useForm<ExportFormValues>({
    resolver: useI18nResolver(exportFormSchema),
    defaultValues: { kind: defaultKind ?? 'orders_csv', from: '', to: '' },
  });
  const kind = useWatch({ control: form.control, name: 'kind' });

  const onSubmit = form.handleSubmit((values) =>
    create.mutate(toExportBody(values), {
      onSuccess: (job) => setTracked({ id: job.id, startedAt: Date.now() }),
      onError: (error) => {
        const applied = applyFieldErrors(error, form.setError, {
          knownFields: KNOWN_FIELDS,
          mapField: (field) => mapField(field),
        });
        if (!applied) toast.error(message(error));
      },
    }),
  );

  const job = tracker.job;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[15px]">{t('export.title')}</CardTitle>
        <CardDescription>{t('export.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Form {...form}>
          <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4" noValidate>
            <FormField
              control={form.control}
              name="kind"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('export.kind')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(ExportKind).map((value) => (
                        <SelectItem key={value} value={value}>
                          {t(`kinds.${value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {kind === 'orders_csv' ? (
              <fieldset className="grid grid-cols-2 gap-3">
                <legend className="text-muted-foreground mb-2 text-xs">
                  {t('export.placedRange')}
                </legend>
                <FormField
                  control={form.control}
                  name="from"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('export.from')}</FormLabel>
                      <FormControl>
                        <DatePicker {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="to"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('export.to')}</FormLabel>
                      <FormControl>
                        <DatePicker {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </fieldset>
            ) : null}
            <Button type="submit" disabled={create.isPending} className="self-start">
              <FileDown aria-hidden />
              {create.isPending ? t('export.starting') : t('export.start')}
            </Button>
          </form>
        </Form>
        {job ? (
          <TrackedJobStatus
            tracker={tracker}
            title={t('export.latest', { kind: t(`kinds.${job.kind}`) })}
          >
            {job.status === 'completed' && job.rowCount !== null ? (
              <p className="text-muted-foreground text-xs">
                {t('export.rows', { count: job.rowCount, rows: format.number(job.rowCount) })}
              </p>
            ) : null}
            {job.ready ? (
              <Button
                type="button"
                size="sm"
                className="self-start"
                disabled={download.isPending}
                onClick={() => download.mutate(job)}
              >
                <Download aria-hidden />
                {t('download.label')}
              </Button>
            ) : null}
          </TrackedJobStatus>
        ) : null}
      </CardContent>
    </Card>
  );
}
